import { motion } from "framer-motion";
import {
  ArrowRight,
  Brain,
  CheckCircle2,
  Cloud,
  Compass,
  Cpu,
  Database,
  HeartHandshake,
  Layers,
  Lightbulb,
  Rocket,
  ShieldCheck,
  Sparkles,
  Target,
  Trophy,
  Users,
} from "lucide-react";
import "./AboutUs.css";
import { Link } from "react-router-dom";
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  show: { transition: { staggerChildren: 0.12 } },
};

const reasons = [
  {
    icon: Users,
    title: "Dedicated Engineering Teams",
    desc: "Teams focused exclusively on your products, platforms and business objectives — a true extension of your organization.",
    bullets: ["Single-customer focus", "Embedded into your culture", "Long-term ownership"],
  },
  {
    icon: Sparkles,
    title: "AI-Native Delivery Model",
    desc: "AI embedded from day one — assisted development, automated testing, intelligent documentation and productivity tools.",
    bullets: ["AI-assisted coding", "Automated QA & docs", "Faster iteration cycles"],
  },
  {
    icon: Rocket,
    title: "Accelerated Innovation",
    desc: "Increase engineering capacity, launch faster and run parallel innovation tracks without stretching internal teams.",
    bullets: ["Parallel roadmaps", "Rapid prototyping", "Time-to-market boost"],
  },
  {
    icon: Trophy,
    title: "World-Class Talent",
    desc: "Highly skilled engineers across AI/ML, product, cloud, data, DevOps, embedded systems and enterprise software.",
    bullets: ["Top 5% engineering pool", "Multi-disciplinary teams", "Senior tech leadership"],
  },
  {
    icon: ShieldCheck,
    title: "Full Ownership & Control",
    desc: "You own the teams, IP, processes, technology assets and long-term engineering capability — for good.",
    bullets: ["100% IP ownership", "Transparent processes", "No vendor lock-in"],
  },
  {
    icon: Compass,
    title: "Build–Operate–Transfer",
    desc: "A proven BOT model that lets you transition into a fully operational offshore R&D center on your timeline.",
    bullets: ["Build the team", "Operate end-to-end", "Transfer when ready"],
  },
];

const talents = [
  {
    icon: Brain,
    label: "AI & Machine Learning",
    desc: "LLMs, computer vision, MLOps, generative AI and intelligent automation.",
    chips: ["LLMs", "RAG", "MLOps", "Vision"],
  },
  {
    icon: Layers,
    label: "Product Engineering",
    desc: "Full-stack product teams designing and shipping world-class SaaS experiences.",
    chips: ["React", "Node.js", "Mobile", "UX"],
  },
  {
    icon: Cloud,
    label: "Cloud & Platform",
    desc: "Cloud-native architectures on AWS, Azure and GCP — secure, scalable, observable.",
    chips: ["AWS", "Azure", "GCP", "K8s"],
  },
  {
    icon: Database,
    label: "Data Engineering & Analytics",
    desc: "Modern data platforms, pipelines, lakehouses and business intelligence.",
    chips: ["Snowflake", "Spark", "dbt", "BI"],
  },
  {
    icon: Sparkles,
    label: "DevOps & Automation",
    desc: "CI/CD, infrastructure-as-code and reliability engineering at scale.",
    chips: ["CI/CD", "Terraform", "SRE", "GitOps"],
  },
  {
    icon: Cpu,
    label: "Embedded Systems & IoT",
    desc: "Firmware, edge computing and connected device platforms for smart products.",
    chips: ["Firmware", "Edge", "RTOS", "IoT"],
  },
];

const values = [
  {
    icon: Target,
    label: "Customer Focused",
    desc: "Every decision starts with the customer outcome — never internal convenience.",
  },
  {
    icon: HeartHandshake,
    label: "Passion & Empathy",
    desc: "We care deeply about the people we work with and the problems we solve.",
  },
  {
    icon: ShieldCheck,
    label: "Trust, Accountability, Integrity",
    desc: "We do what we say. We own outcomes — the good and the difficult.",
  },
  {
    icon: Lightbulb,
    label: "Value Generation",
    desc: "We measure ourselves by the measurable value we create for our partners.",
  },
  {
    icon: Users,
    label: "People First",
    desc: "Our people build everything. Their growth and wellbeing come before all else.",
  },
];

const culture = [
  {
    no: "01",
    title: "Hiring for Attitude",
    desc: "We hire mindset, ownership and learning agility — skills follow.",
    points: ["Mindset over resume", "Long-term cultural fit", "Growth potential"],
  },
  {
    no: "02",
    title: "On-Boarding",
    desc: "A structured journey that ramps engineers from day one with clarity and confidence.",
    points: ["HR onboarding", "Structured induction", "Buddy & mentor program"],
  },
  {
    no: "03",
    title: "Cultural Fitment Training",
    desc: "Tailored training to seamlessly collaborate with global teams across geographies.",
    points: ["Working with US / Europe", "Professional etiquette", "Async communication"],
  },
  {
    no: "04",
    title: "Internal Knowledge Sharing",
    desc: "Continuous learning loops powered by community, clients and external experts.",
    points: ["External events", "Cultural training from clients", "Internal tech talks"],
  },
  {
    no: "05",
    title: "Technology Trainings",
    desc: "Ongoing upskilling on modern frameworks, AI workflows and emerging tooling.",
    points: ["Continuous upskilling", "Modern tooling & AI workflows", "Certifications & labs"],
  },
];

const heroImages = [
  "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80",
  "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80",
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&q=80",
  "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80",
];

const AboutUs = () => {
  return (
    <div className="au-page">
      {/* HERO */}
      <section className="au-hero py-5">
        <div className="au-hero__grid" />
        <div className="au-hero__blob au-hero__blob--a" />
        <div className="au-hero__blob au-hero__blob--b" />

        <div className="au-container au-hero__inner container">
          <motion.div
            className="au-hero__copy"
            initial="hidden"
            animate="show"
            variants={stagger}
          >
            <motion.span variants={fadeUp} className="au-eyebrow">
              <Sparkles size={14} /> About SpringUp Labs
            </motion.span>
            <motion.h1 variants={fadeUp} className="au-hero__title">
              Building <span className="au-grad">AI-Native</span> Engineering
              Organizations for Global SMEs
            </motion.h1>
            <motion.p variants={fadeUp} className="au-hero__lead">
              In today's rapidly evolving technology landscape, innovation has
              become the single biggest differentiator for growth. Yet many
              SMEs struggle to scale product development, access specialized
              talent, and keep pace with emerging technologies such as AI,
              cloud computing, and automation. SpringUp Labs was founded to
              address this challenge.
            </motion.p>
            <motion.div variants={fadeUp} className="au-hero__cta">
              <a className="au-btn au-btn--primary" href="#story">
                Our Story <ArrowRight size={16} />
              </a>
              <a className="au-btn au-btn--ghost" href="#values">
                Core Values
              </a>
            </motion.div>

            <motion.div variants={fadeUp} className="au-hero__stats">
              <div>
                <strong>30–40%</strong>
                <span>Cost reduction</span>
              </div>
              <div>
                <strong>100%</strong>
                <span>IP ownership</span>
              </div>
              <div>
                <strong>AI-Native</strong>
                <span>Delivery model</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="au-hero__visual"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="au-collage">
              {heroImages.map((src, i) => (
                <div key={i} className={`au-collage__tile au-collage__tile--${i}`}>
                  <img src={src} alt="Team" loading="lazy" />
                </div>
              ))}
              <div className="au-collage__badge">
                <Sparkles size={16} />
                <div>
                  <strong>Build • Operate • Transfer</strong>
                  <span>Proven engagement model</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      <section id="story" className="au-section au-story">
        <div className="au-container au-story__grid container">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="au-story__copy"
          >
            <motion.span variants={fadeUp} className="au-eyebrow au-eyebrow--alt">
              Our Story
            </motion.span>
            <motion.h2 variants={fadeUp} className="au-h2">
              An AI-native R&amp;D and engineering partner for global SMEs.
            </motion.h2>
            <motion.p variants={fadeUp} className="au-p">
              We are an AI-native R&amp;D and engineering partner helping
              global SMEs build dedicated offshore engineering teams through a
              proven Build–Operate–Transfer (BOT) model. Our mission is simple:
              enable ambitious companies to access world-class engineering
              capabilities, accelerate innovation, and build sustainable
              competitive advantage without the complexity and risk of
              establishing offshore operations on their own.
            </motion.p>
            <motion.p variants={fadeUp} className="au-p">
              We combine deep engineering expertise, AI-driven delivery
              frameworks, and product-centric execution to help organizations
              reduce engineering costs by <strong>30–40%</strong>, improve
              development velocity, and scale technology teams with confidence.
            </motion.p>
            <motion.p variants={fadeUp} className="au-p">
              Unlike traditional outsourcing models that focus on project
              execution or resource augmentation, SpringUp Labs helps companies
              build their own engineering capability. Every team we build is
              dedicated to a single customer, aligned to their culture,
              processes, product vision, and long-term business objectives.
            </motion.p>
            <motion.p variants={fadeUp} className="au-p">
              Our teams function as a seamless extension of your organization,
              enabling you to retain complete control over priorities,
              intellectual property, product roadmaps, and engineering
              practices. As the team matures, ownership can be seamlessly
              transferred, giving you a fully operational offshore R&amp;D
              center that you own and control.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="au-story__card"
          >
            <div className="au-story__cardInner">
              <div className="au-story__icon">
                <Sparkles size={22} />
              </div>
              <h3>Our Promise</h3>
              <p className="au-story__promise">
                You own the team. You own the IP. You own the roadmap. You own
                the future.
              </p>
              <div className="au-story__pill">
                <ShieldCheck size={14} /> Full ownership &amp; control
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY */}
      <section className="au-section au-why">
        <div className="au-container container">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="au-secHead"
          >
            <motion.span variants={fadeUp} className="au-eyebrow au-eyebrow--alt">
              Why SpringUp Labs
            </motion.span>
            <motion.h2 variants={fadeUp} className="au-h2">
              Built for ambitious global SMEs.
            </motion.h2>
            <motion.p variants={fadeUp} className="au-p">
              Six reasons high-growth companies choose SpringUp Labs as their
              long-term engineering partner.
            </motion.p>
          </motion.div>

          <motion.div
            className="au-why__grid"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            variants={stagger}
          >
            {reasons.map((r, i) => (
              <motion.div key={i} variants={fadeUp} className="au-rcard">
                <span className="au-rcard__num">{String(i + 1).padStart(2, "0")}</span>
                <div className="au-rcard__icon">
                  <r.icon size={20} />
                </div>
                <h3>{r.title}</h3>
                <p>{r.desc}</p>
                <ul className="au-rcard__list">
                  {r.bullets.map((b, j) => (
                    <li key={j}>
                      <CheckCircle2 size={14} /> {b}
                    </li>
                  ))}
                </ul>
                <span className="au-rcard__corner" />
                <span className="au-rcard__glow" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* TALENT */}
      <section className="au-section au-talent">
        <div className="au-container container">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="au-secHead au-secHead--center"
          >
            <motion.span variants={fadeUp} className="au-eyebrow au-eyebrow--alt">
              Access to World-Class Talent
            </motion.span>
            <motion.h2 variants={fadeUp} className="au-h2">
              Highly skilled engineers across the modern stack.
            </motion.h2>
          </motion.div>

          <motion.div
            className="au-talent__grid"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
          >
            {talents.map((t, i) => (
              <motion.div key={i} variants={fadeUp} className="au-tcard">
                <div className="au-tcard__head">
                  <div className="au-tcard__icon">
                    <t.icon size={22} />
                  </div>
                  <span className="au-tcard__label">{t.label}</span>
                </div>
                <p className="au-tcard__desc">{t.desc}</p>
                <div className="au-tcard__chips">
                  {t.chips.map((c, j) => (
                    <span key={j} className="au-tcard__chip">{c}</span>
                  ))}
                </div>
                <span className="au-tcard__ring" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* VISION */}
      <section className="au-section au-vision">
        <div className="au-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="au-vision__card"
          >
            <span className="au-eyebrow">Our Vision</span>
            <h2 className="au-vision__quote">
              "Helping customers leverage <span className="au-grad">offshore scale</span> and{" "}
              <span className="au-grad">technology innovation</span> — delivered with passion."
            </h2>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section id="values" className="au-section au-values">
        <div className="au-container container">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="au-secHead au-secHead--center"
          >
            <motion.span variants={fadeUp} className="au-eyebrow au-eyebrow--alt">
              Core Values
            </motion.span>
            <motion.h2 variants={fadeUp} className="au-h2">
              The principles that shape every decision we make.
            </motion.h2>
          </motion.div>

          <motion.div
            className="au-values__grid"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
          >
            {values.map((v, i) => (
              <motion.div key={i} variants={fadeUp} className="au-vcard">
                <span className="au-vcard__bg" />
                <div className="au-vcard__icon">
                  <v.icon size={22} />
                </div>
                <h3>{v.label}</h3>
                {/* <p>{v.desc}</p> */}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CULTURE */}
      {/* <section className="au-section au-culture">
        <div className="au-container">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="au-secHead au-secHead--center"
          >
            <motion.span variants={fadeUp} className="au-eyebrow au-eyebrow--alt">
              Cultural Alignment
            </motion.span>
            <motion.h2 variants={fadeUp} className="au-h2">
              A 5-step journey that builds aligned, high-performing teams.
            </motion.h2>
          </motion.div>

          <div className="au-timeline">
            <div className="au-timeline__line" />
            {culture.map((c, i) => (
              <motion.div
                key={i}
                className={`au-step ${i % 2 === 0 ? "au-step--left" : "au-step--right"}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
              >
                <div className="au-step__dot">{c.no}</div>
                <div className="au-step__card">
                  <span className="au-step__tag">Step {c.no}</span>
                  <h3>{c.title}</h3>
                  <p className="au-step__desc">{c.desc}</p>
                  <ul>
                    {c.points.map((p, j) => (
                      <li key={j}>{p}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}
      {/* CULTURE */}
      <section className="au-section au-culture">
        <div className="au-container container">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="au-secHead au-secHead--center"
          >
            <motion.span variants={fadeUp} className="au-eyebrow au-eyebrow--alt">
              Cultural Alignment
            </motion.span>
            <motion.h2 variants={fadeUp} className="au-h2">
              A 5-step journey that builds aligned, high-performing teams.
            </motion.h2>
          </motion.div>

          <div className="au-steps">
            {culture.map((c, i) => (
              <motion.div
                key={i}
                className="au-step2__item"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
              >
                {/* Number + connecting line */}
                <div className="au-step2__track">
                  <div className="au-step2__ring">
                    <span className="au-step2__no">{c.no}</span>
                  </div>
                  {i < culture.length - 1 && <div className="au-step2__line" />}
                </div>

                {/* Card */}
                <div className="au-step2__card">
                  <div className="au-step2__topbar" />
                  <h3>{c.title}</h3>
                  <hr className="au-step2__hr" />
                  {c.points.length > 0 && (
                    <div className="au-step2__chips">
                      {c.points.map((p, j) => (
                        <span key={j} className="au-step2__chip">
                          {p}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="au-section au-cta">
        <div className="au-container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="au-cta__card"
          >
            <h2>SpringUp Labs is the partner of choice for global SMEs.</h2>
            <p>
              Build dedicated offshore R&D centers, accelerate digital innovation, and leverage AI to create lasting business value.
            </p>
            <Link to="/contact" className="au-btn au-btn--primary au-btn--lg">
              Talk to us <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;